// variable-trace — the PURE half of the guided-make "Show my variables" readout (Trello lFu60Fgm).
//
// A student asked for "a trace that showed the values inside the variable". This module builds a
// console-Python DRIVER that runs the student's current code once, in the OFF-SCREEN hidden runner
// (`hidden-runner.js`, reused as-is), under a bounded `sys.settrace` tracer, and reads back:
//
//   rows   — one row per variable CHANGE made by a top-level line, in run order: [line, name, value]
//            (a loop body line appears once per pass), capped at ROW_CAP;
//   final  — every simple top-level variable's value when the run ended: [name, value].
//
// NO runner-contract change: the driver is ordinary console Python run through the existing
// `requestConsoleRun` path (exactly as compare-harness.js does). Nothing here touches the DOM.
//
// ── What runs ─────────────────────────────────────────────────────────────────────────────
//   * console Python ('python'): the WHOLE program, in a fresh namespace. `input()` meets an empty
//     stdin, so it raises EOFError and the readout says the program stopped there.
//   * a game ('pgzero' / 'pygame' / anything else): the game is NEVER run. The source goes through
//     test-table-grading.js's `buildPgzeroWrapper` (stubs every Pygame Zero name, keeps only
//     top-level defs / classes / non-game imports / assignments, each with its ORIGINAL line
//     number), so the readout shows the values the game STARTS with. Values that change while the
//     game plays (inside update() / on_key_down()) cannot be seen without a runner change.
//
// ── Safety ────────────────────────────────────────────────────────────────────────────────
//   * The source reaches Python as a JSON string decoded with `json.loads` — never spliced as code.
//   * A value is shown only for exact int / float / str / bool / None, and short lists / tuples of
//     those; dicts, sets and longer lists show only their size; any other object shows '<object>'.
//     `repr` is only ever called on those exact built-in types, never on a student's object, so a
//     student class's __repr__ / __str__ never runs. Functions, classes, modules and the game stubs
//     are left out (callable / type checks read the TYPE, never the object). Names starting with
//     '_' are left out, and so are the Pygame Zero window settings (WIDTH, HEIGHT, TITLE, ICON).
//   * Bounded three ways: an event cap and a wall clock inside the tracer (it raises a private
//     BaseException the wrapper's `except Exception` cannot swallow), and the host-side
//     `runWithTimeout` budget in the chrome, which restarts ONLY the hidden runner.
//   * The page renders every name/value with textContent only; `readTraceOutput` additionally
//     strips control characters and truncates.

import { buildPgzeroWrapper } from './test-table-grading.js';

export var SENTINEL = '@@PC-VARS@@';
export var ROW_CAP = 50;
export var FINAL_CAP = 60;
export var VALUE_MAX = 80;
export var NAME_MAX = 60;
export var LIST_MAX = 10;
export var EVENT_CAP = 200000;
export var WALL_SECONDS = 1.2;   // under RUN_BUDGET_MS, so the driver stops itself and still prints
/** The host-side budget for one readout run (the program's own run time, after boot). */
export var RUN_BUDGET_MS = 2000;
/** Pygame Zero window settings — real variables, but settings rather than game state. */
export var GAME_SETTING_NAMES = ['WIDTH', 'HEIGHT', 'TITLE', 'ICON'];

/** A Python string literal holding `text` (JSON escapes are all legal Python escapes). */
function pyStr(text) {
  return JSON.stringify(JSON.stringify(typeof text === 'string' ? text : ''));
}

/** True when `mode` runs the whole program (console Python); otherwise it is a game. */
export function isConsoleMode(mode) {
  return mode === 'python';
}

/**
 * The driver program for `source`.
 *   opts.mode  the HOST runner language: 'python' runs the program; anything else is a game and
 *              runs through `buildPgzeroWrapper` (stubs on).
 */
export function buildTraceProgram(source, opts) {
  var mode = opts && opts.mode;
  var consoleMode = isConsoleMode(mode);
  var program = consoleMode ? (typeof source === 'string' ? source : '') : buildPgzeroWrapper(source, { stubs: true });
  var cfg = {
    rowCap: ROW_CAP,
    eventCap: EVENT_CAP,
    wall: WALL_SECONDS,
    valueCap: VALUE_MAX,
    listMax: LIST_MAX,
    // The student's own code is compiled as "main.py" (console mode compiles it directly; the
    // wrapper compiles each kept statement with `__file__`, which the namespace sets to it).
    // The wrapper itself is compiled under a private name so its helpers are never traced.
    codeFile: consoleMode ? 'main.py' : '<pc-vars-wrapper>',
    skip: consoleMode ? [] : GAME_SETTING_NAMES
  };
  return [
    'import sys as _pc_vsys',
    'import json as _pc_vjson',
    'import time as _pc_vtime',
    'import types as _pc_vtypes',
    '_pc_vprint = print',
    '_pc_vcfg = _pc_vjson.loads(' + JSON.stringify(JSON.stringify(cfg)) + ')',
    '_pc_vsrc = _pc_vjson.loads(' + pyStr(program) + ')',
    '_pc_vfile = "main.py"',
    '_pc_vskip = set(_pc_vcfg["skip"])',
    '_pc_vsimple = (int, float, str, bool, type(None))',
    '',
    'class _PcVarsStop(BaseException):',
    '    pass',
    '',
    '_pc_vst = {"rows": [], "events": 0, "t0": 0.0, "stopped": None, "truncated": False, "last": {}}',
    '',
    // repr() only ever runs on an EXACT built-in scalar here.
    'def _pc_vscalar(v, cap):',
    '    t = type(v)',
    '    if t is str:',
    '        s = repr(v[:cap])',
    '        return s[:-1] + "..." + s[-1] if len(v) > cap else s',
    '    if t is int:',
    '        try:',
    '            s = repr(v)',
    '        except ValueError:',
    '            return "<a very big number>"',
    '        return s[:cap] + "..." if len(s) > cap else s',
    '    return repr(v)',
    '',
    'def _pc_vplural(n):',
    '    return str(n) + (" item" if n == 1 else " items")',
    '',
    // The display string for one value, or None when it is not a variable worth showing.
    // Only TYPE checks (type(v), issubclass, callable) touch an unknown object.
    'def _pc_vshow(v):',
    '    cap = _pc_vcfg["valueCap"]',
    '    t = type(v)',
    '    if t in _pc_vsimple:',
    '        return _pc_vscalar(v, cap)',
    '    if t is list or t is tuple:',
    '        n = len(v)',
    '        if n <= _pc_vcfg["listMax"] and all(type(x) in _pc_vsimple for x in v):',
    '            parts = ", ".join(_pc_vscalar(x, 20) for x in v)',
    '            s = "[" + parts + "]" if t is list else "(" + parts + ("," if n == 1 else "") + ")"',
    '            return s[:cap] + "..." if len(s) > cap else s',
    '        return "<" + t.__name__ + ", " + _pc_vplural(n) + ">"',
    '    if t is dict or t is set:',
    '        return "<" + t.__name__ + ", " + _pc_vplural(len(v)) + ">"',
    '    if issubclass(t, type) or issubclass(t, _pc_vtypes.ModuleType) or callable(v):',
    '        return None',
    '    return "<object>"',
    '',
    'def _pc_vsnap(g):',
    '    out = {}',
    '    for k, v in list(g.items()):',
    '        if type(k) is not str or k.startswith("_") or k in _pc_vskip:',
    '            continue',
    '        s = _pc_vshow(v)',
    '        if s is not None:',
    '            out[k] = s',
    '    return out',
    '',
    // Record every change since the last snapshot against `line` (the line that just ran).
    'def _pc_vdiff(g, line):',
    '    st = _pc_vst',
    '    if st["truncated"]:',
    '        return',
    '    snap = _pc_vsnap(g)',
    '    last = st["last"]',
    '    for k, s in snap.items():',
    '        if last.get(k) != s:',
    '            if len(st["rows"]) >= _pc_vcfg["rowCap"]:',
    '                st["truncated"] = True',
    '                break',
    '            st["rows"].append([line, k, s])',
    '    st["last"] = snap',
    '',
    'def _pc_vtick():',
    '    st = _pc_vst',
    '    if st["stopped"]:',
    '        raise _PcVarsStop()',
    '    st["events"] += 1',
    '    if st["events"] > _pc_vcfg["eventCap"]:',
    '        st["stopped"] = "steps"',
    '        raise _PcVarsStop()',
    '    if st["events"] % 500 == 0 and _pc_vtime.monotonic() - st["t0"] > _pc_vcfg["wall"]:',
    '        st["stopped"] = "time"',
    '        raise _PcVarsStop()',
    '',
    // Lines inside the student's own functions are counted (the budget), never recorded.
    'def _pc_vinner(frame, event, arg):',
    '    if event == "line":',
    '        _pc_vtick()',
    '    return _pc_vinner',
    '',
    // A student MODULE frame: on each line, record what the PREVIOUS line changed.
    'def _pc_vmodule():',
    '    prev = [None]',
    '    def local(frame, event, arg):',
    '        if event == "line":',
    '            if prev[0] is not None and not _pc_vst["stopped"]:',
    '                _pc_vdiff(frame.f_globals, prev[0])',
    '            prev[0] = frame.f_lineno',
    '            _pc_vtick()',
    '        elif event == "return":',
    '            if prev[0] is not None and not _pc_vst["stopped"]:',
    '                _pc_vdiff(frame.f_globals, prev[0])',
    '            prev[0] = None',
    '        return local',
    '    return local',
    '',
    'def _pc_vglobal(frame, event, arg):',
    '    code = frame.f_code',
    '    if code.co_filename != _pc_vfile:',
    '        return None',
    '    if code.co_name == "<module>":',
    '        if not _pc_vst["stopped"] and not _pc_vst["truncated"]:',
    '            _pc_vst["last"] = _pc_vsnap(frame.f_globals)',
    '        return _pc_vmodule()',
    '    _pc_vtick()',
    '    return _pc_vinner',
    '',
    '_pc_vns = {"__name__": "__main__", "__file__": _pc_vfile}',
    '_pc_verr = None',
    'try:',
    '    _pc_vcode = compile(_pc_vsrc, _pc_vcfg["codeFile"], "exec")',
    '    _pc_vst["t0"] = _pc_vtime.monotonic()',
    '    _pc_vsys.settrace(_pc_vglobal)',
    '    try:',
    '        exec(_pc_vcode, _pc_vns)',
    '    finally:',
    '        _pc_vsys.settrace(None)',
    'except _PcVarsStop:',
    '    pass',
    'except SystemExit:',
    '    pass',
    'except KeyboardInterrupt:',
    '    raise',
    'except BaseException as _pc_ve:',
    '    _pc_vline = None',
    '    if isinstance(_pc_ve, SyntaxError) and _pc_ve.filename == _pc_vfile:',
    '        _pc_vline = _pc_ve.lineno',
    '    _pc_vtb = _pc_ve.__traceback__',
    '    while _pc_vtb is not None:',
    '        if _pc_vtb.tb_frame.f_code.co_filename == _pc_vfile:',
    '            _pc_vline = _pc_vtb.tb_lineno',
    '        _pc_vtb = _pc_vtb.tb_next',
    '    _pc_verr = {"type": type(_pc_ve).__name__, "line": _pc_vline}',
    '_pc_vsys.settrace(None)',
    '_pc_vout = {"v": 1, "rows": _pc_vst["rows"], "final": [[k, s] for k, s in _pc_vsnap(_pc_vns).items()],',
    '            "truncated": _pc_vst["truncated"], "stopped": _pc_vst["stopped"], "error": _pc_verr}',
    '_pc_vprint("\\n' + SENTINEL + '" + _pc_vjson.dumps(_pc_vout), flush=True)',
    ''
  ].join('\n');
}

// ── reading the result ────────────────────────────────────────────────────────────────────

// C0 controls, DEL and the C1 range (built from code points so no raw control character ever
// sits in this source file).
var CONTROL_CHARS = new RegExp('[' + String.fromCharCode(0) + '-' + String.fromCharCode(0x1f) +
  String.fromCharCode(0x7f) + '-' + String.fromCharCode(0x9f) + ']', 'g');

/** Control characters (C0, DEL, C1) become spaces; then truncate with an ellipsis. */
export function cleanText(value, max) {
  var s = String(value == null ? '' : value).replace(CONTROL_CHARS, ' ');
  var cap = typeof max === 'number' && max > 0 ? max : VALUE_MAX;
  return s.length > cap ? s.slice(0, cap) + '…' : s;
}

function isLine(n) {
  return typeof n === 'number' && Math.floor(n) === n && n > 0 && n < 1e7;
}

function readPairs(list, cap) {
  var out = [];
  (Array.isArray(list) ? list : []).forEach(function (p) {
    if (out.length >= cap) { return; }
    if (!Array.isArray(p) || p.length !== 2 || typeof p[0] !== 'string' || typeof p[1] !== 'string' || !p[0]) { return; }
    out.push({ name: cleanText(p[0], NAME_MAX), value: cleanText(p[1], VALUE_MAX) });
  });
  return out;
}

/**
 * The LAST sentinel line in `stdout` -> { rows, final, truncated, stopped, error }, or null when
 * there is none or it is malformed. A student's own printed text can never be mistaken for it
 * (only the last sentinel counts, and the driver prints its line after the program finishes).
 *   rows    [{ line, name, value }]  ≤ ROW_CAP
 *   final   [{ name, value }]        ≤ FINAL_CAP
 *   stopped null | 'steps' | 'time'
 *   error   null | { type, line|null }
 */
export function readTraceOutput(stdout) {
  var text = stdout == null ? '' : String(stdout);
  var at = text.lastIndexOf(SENTINEL);
  if (at === -1) { return null; }
  var line = text.slice(at + SENTINEL.length).split('\n')[0];
  var obj;
  try { obj = JSON.parse(line); } catch (e) { return null; }
  if (!obj || typeof obj !== 'object' || obj.v !== 1) { return null; }

  var rows = [];
  var rawRows = Array.isArray(obj.rows) ? obj.rows : [];
  rawRows.forEach(function (r) {
    if (rows.length >= ROW_CAP) { return; }
    if (!Array.isArray(r) || r.length !== 3 || !isLine(r[0]) || typeof r[1] !== 'string' || !r[1] || typeof r[2] !== 'string') { return; }
    rows.push({ line: r[0], name: cleanText(r[1], NAME_MAX), value: cleanText(r[2], VALUE_MAX) });
  });

  var final = readPairs(obj.final, FINAL_CAP);
  var stopped = (obj.stopped === 'steps' || obj.stopped === 'time') ? obj.stopped : null;
  var error = null;
  if (obj.error && typeof obj.error === 'object' && typeof obj.error.type === 'string' && obj.error.type) {
    error = { type: cleanText(obj.error.type, 40), line: isLine(obj.error.line) ? obj.error.line : null };
  }
  return {
    rows: rows,
    final: final,
    truncated: obj.truncated === true || rawRows.length > ROW_CAP,
    finalTruncated: Array.isArray(obj.final) && obj.final.length > FINAL_CAP,
    stopped: stopped,
    error: error
  };
}

// ── plain-English copy ────────────────────────────────────────────────────────────────────

export var COPY = {
  heading: 'Variables',
  intro: 'See the values your variables hold.',
  show: 'Show my variables',
  refresh: 'Refresh',
  hide: 'Hide',
  running: 'Running your code to read its variables…',
  empty: 'No variables yet. Make one with a line like score = 0.',
  gameNote: 'These are the values your game starts with. The game itself is not played here, so values that change while you play (in update() or on_key_down()) are not shown.',
  timeout: 'Your code took too long, so it was stopped. Look for a loop that never ends.',
  failed: 'Your variables could not be read this time. Try again.',
  truncated: 'Only the first ' + ROW_CAP + ' changes are shown.',
  stopped: 'Your code ran for a long time, so it was stopped early. The values are from when it stopped.',
  finalHeading: 'Values now',
  changesSummary: 'Show each change',
  nameCol: 'Variable',
  valueCol: 'Value',
  lineCol: 'Line'
};

/** The one-sentence explanation of an error the run stopped on ('' when there is none). */
export function errorMessage(error) {
  if (!error) { return ''; }
  var at = error.line ? ' at line ' + error.line : '';
  // Plain CPython raises EOFError at input(); the browser runner's empty stdin raises its own
  // `_StdinExhausted`. Both mean "this program asks for input".
  if (error.type === 'EOFError' || error.type === '_StdinExhausted') {
    return 'Your program stopped' + at + ' because it asks for input, so the values are from before that line.';
  }
  if (error.type === 'SyntaxError' || error.type === 'IndentationError' || error.type === 'TabError') {
    return 'Your code has a mistake' + at + ' (' + error.type + '), so it could not run. Fix it and try again.';
  }
  return 'Your code stopped with an error' + at + ' (' + error.type + '). The values are from before it stopped.';
}

/**
 * Turn one hidden-runner answer into what the panel shows.
 *   res   the `runWithTimeout` answer: { status: 'ok', value: { stdout } } | { status: 'timeout' } | …
 *   mode  the host language ('python' = console; anything else = a game)
 * Returns { ok, notes: [string], rows, final, truncated }. `notes` are plain sentences, in order.
 */
export function describeRun(res, mode) {
  if (!res || res.status === 'error' || res.status === 'lost') {
    return { ok: false, notes: [COPY.failed], rows: [], final: [], truncated: false };
  }
  if (res.status === 'timeout') {
    return { ok: false, notes: [COPY.timeout], rows: [], final: [], truncated: false };
  }
  var parsed = readTraceOutput(res.value && res.value.stdout);
  if (!parsed) {
    return { ok: false, notes: [COPY.failed], rows: [], final: [], truncated: false };
  }
  var notes = [];
  if (!isConsoleMode(mode)) { notes.push(COPY.gameNote); }
  if (parsed.error) { notes.push(errorMessage(parsed.error)); }
  if (parsed.stopped) { notes.push(COPY.stopped); }
  if (!parsed.final.length) { notes.push(COPY.empty); }
  if (parsed.truncated) { notes.push(COPY.truncated); }
  return { ok: true, notes: notes, rows: parsed.rows, final: parsed.final, truncated: parsed.truncated };
}
